import { locateProxy } from '#~/agent/index.js';
import { useIsEmbeddedBridgeOnly } from '#~/agent/hooks.js';
import { selectVisibleSectionLabels } from '#~/video/components/settings/layout/section-visibility.js';
import type { Device } from '@studio/models/device';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { DeviceSetting as PBDeviceSetting } from '@tryiris-ai/common.protojs/agent/v1/device_settings/device_settings';
import { DeviceRegistryInterfaceSectionType } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { useMemo } from 'react';
import { useWiring } from './wiring.js';
// import { useMount } from '@studio/react-utils/hooks'; // used for debugging

/**
 * Helper function to check if a section type is an advanced settings category.
 * This includes all ADV_SETTINGS_* categories by checking for the ADV_SETTINGS substring in the key name,
 * with a fallback to the numeric range (100-127) for backwards compatibility.
 *
 * @param sectionType - The section type to check
 * @returns True if the section type is an advanced settings category
 */
export const isAdvancedSettingsCategory = (
  sectionType: DeviceRegistryInterfaceSectionType
): boolean => {
  return (
    DeviceRegistryInterfaceSectionType[sectionType]?.includes('ADV_SETTINGS') ||
    (sectionType >= 100 && sectionType <= 127)
  );
};

/**
 * Custom hook to get the advanced settings interface sections for a device.
 * Now supports the new ADV_SETTINGS_* categories by checking for the ADV_SETTINGS substring in the key name.
 *
 * @param deviceId - The device ID
 * @returns The advanced settings interface sections for the device
 */
export const useAdvancedSettings = (deviceId: Device['id']) => {
  const agent = locateProxy(deviceId);
  const { interfaceSections } = agent?.state.status?.device[deviceId]?.deviceSettings ?? {};

  return Object.values(interfaceSections ?? {}).filter((section) =>
    isAdvancedSettingsCategory(section.type)
  );
};

/**
 * Custom hook to get the advanced settings interface sections for a specific section group.
 *
 * @param deviceId - The device ID
 * @param sectionGroup - The section group to filter by (e.g., 'General', 'Feature', 'Network')
 * @returns The advanced settings interface sections for the specified section group
 */
export const useAdvancedSettingsForSectionGroup = (
  deviceId: Device['id'],
  sectionGroup: string
) => {
  const agent = locateProxy(deviceId);
  const { interfaceSections } = agent?.state.status?.device[deviceId]?.deviceSettings ?? {};

  return Object.values(interfaceSections ?? {}).filter((section) => {
    if (!isAdvancedSettingsCategory(section.type)) return false;
    return getSectionGroupFromSectionType(section.type) === sectionGroup;
  });
};

/**
 * Helper function to map ADV_SETTINGS section types to section group names.
 * This provides the UI grouping logic that was previously handled by the Device Registry.
 *
 * @param sectionType - The section type to map
 * @returns The section group name for UI grouping
 */
export const getSectionGroupFromSectionType = (
  sectionType: DeviceRegistryInterfaceSectionType
): string => {
  // Map ADV_SETTINGS section types to section groups for UI grouping
  switch (sectionType) {
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_GENERAL:
      return 'General';
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_GENERAL:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_HDMI:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_SDI:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_NDI:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_USB:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_1:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_2:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_3:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_4:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_5:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_6:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_RTMP:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_SRT:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_RTSP:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_VIDEO_VLOG:
      return 'Video';
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_AUDIO_GENERAL:
      return 'Audio';
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_CONTROLS:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_PRESETS_NATIVE:
      return 'Controls';
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_MONITORING:
      return 'Monitoring';
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_DEVICE_INFO:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_SYSTEM:
      return 'System';
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_NETWORK:
      return 'Network';
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_ASSISTANCE:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OSD:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_FEATURES:
    case DeviceRegistryInterfaceSectionType.ADV_SETTINGS_TOOLS:
      return 'Features';
    default:
      return 'Video';
  }
};

/**
 * Canonical tab order for consistent UI across all camera models. It is the
 * single order list for the advanced settings tabs: predefined tabs and
 * registry-derived tabs alike are sorted through it by `orderSettingsTabs`, so
 * adding a tab in one place cannot make the rendered order and the order test
 * disagree.
 *
 * Two deliberate absences. 'Network' is retired — its registry section is no
 * longer registered, and a tab with no sections is a dead end. 'Controls' is
 * the registry's name for the group the predefined 'Control' tab renders; the
 * tab is listed under its own name and withheld when that group has nothing
 * visible (see `orderSettingsTabs`). The vendor connection tab is absent too:
 * its label varies per whitelabel variant, so it cannot be listed by literal
 * name and leads the union instead.
 */
export const CANONICAL_TAB_ORDER = ['Video', 'Audio', 'Control', 'Features', 'System'] as const;

/**
 * The Control tab's section-group names: the predefined tab ('Control') and the
 * registry group it renders ('Controls', kept so a registry-derived group can
 * never reintroduce the tab by the old name). `orderSettingsTabs` withholds both
 * on an embedded device, which cannot drive camera control, and whenever the
 * Controls group has nothing visible: the tab's only content is that group —
 * the platform control settings live on Iris Connection → Control — so without
 * it the tab would open on "No settings available".
 */
export const CONTROL_SECTION_GROUPS = new Set(['Control', 'Controls']);

/** The registry section group the Control tab renders (see control-section.tsx). */
const CONTROLS_REGISTRY_GROUP = 'Controls';

/**
 * Section groups listed in CANONICAL_TAB_ORDER purely as an order anchor: their
 * tab is rendered from PREDEFINED_SECTIONS, so useSectionGroups must not derive
 * a second one from the registry.
 */
const PREDEFINED_SECTION_GROUPS = new Set<string>(['Control']);

/** A top-level advanced-settings tab, predefined or registry-derived. */
export interface SettingsTab {
  sectionGroup: string;
  panelEnumValue: string;
}

/**
 * Merges the predefined tabs and the registry-derived tabs into the one
 * rendered order, dropping the Control tab when the device is embedded or its
 * Controls group has nothing visible.
 *
 * Pure on purpose: the tab order and the embedded rule are the two things most
 * likely to regress, and both are testable here without mounting the settings
 * scaffold. Callers must derive the active panel from the RESULT — the embedded
 * filter runs inside this function so an embedded device can never auto-correct
 * onto a hidden tab.
 *
 * @param predefined - Tabs rendered from PREDEFINED_SECTIONS, in declaration order
 * @param registryDerived - Tabs derived from the device registry (see useSectionGroups)
 * @param options.isEmbedded - Whether the device is embedded
 * @param options.hasVisibleControls - Whether the registry Controls group has at
 *   least one visible item (see `useHasVisibleControls`)
 * @returns The tabs to render, in canonical order
 */
export const orderSettingsTabs = (
  predefined: ReadonlyArray<SettingsTab>,
  registryDerived: ReadonlyArray<SettingsTab>,
  { hasVisibleControls, isEmbedded }: { hasVisibleControls: boolean; isEmbedded: boolean }
): Array<SettingsTab> => {
  const canonical: ReadonlyArray<string> = CANONICAL_TAB_ORDER;
  const claimed = new Set<string>();
  const union: Array<SettingsTab> = [];
  // One filter for both reasons: the tab either cannot be used (embedded) or
  // would have nothing on it (no visible Controls). Either way it is the dead
  // end this filter exists to prevent.
  const withholdControl = isEmbedded || !hasVisibleControls;

  [...predefined, ...registryDerived].forEach((tab) => {
    if (claimed.has(tab.panelEnumValue)) return;
    if (withholdControl && CONTROL_SECTION_GROUPS.has(tab.sectionGroup)) return;

    claimed.add(tab.panelEnumValue);
    union.push(tab);
  });

  // A tab absent from the canonical list ranks -1 and therefore leads. Today
  // that is only the whitelabel-named connection tab, which is the anchor tab.
  return union
    .map((tab, position) => ({ position, rank: canonical.indexOf(tab.sectionGroup), tab }))
    .sort((a, b) => (a.rank === b.rank ? a.position - b.position : a.rank - b.rank))
    .map(({ tab }) => tab);
};

/**
 * Section labels that start collapsed on first view. `defaultOpen` derived
 * from this set is the INITIAL open state only — Section persists a per-user
 * open preference that overrides it on later views. Exported as a greppable
 * Set so the collapse rule stays testable rather than buried in the renderer.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const DEFAULT_COLLAPSED_SECTIONS = new Set(['Advanced']);

/**
 * Custom hook reporting whether a device is embedded.
 *
 * Falls back to the studio-level embedded check when the device proxy cannot be
 * located, which is the same fallback the section-group filter used before the
 * embedded rule moved into `orderSettingsTabs`.
 *
 * @param deviceId - The device ID
 * @returns True when the device (or the studio hosting it) is embedded
 */
export const useIsDeviceEmbedded = (deviceId: Device['id']): boolean => {
  const isEmbeddedBridgeOnly = useIsEmbeddedBridgeOnly();
  const deviceProxy = locateProxy(deviceId);

  return deviceProxy?.embedded ?? (deviceProxy ? false : isEmbeddedBridgeOnly);
};

/**
 * Whether the device's registry Controls group has at least one item a user can
 * see, under the SAME predicate the Control tab's panel and sub-section strip
 * apply (`selectVisibleSectionLabels`), so the strip and the tab that hosts it
 * cannot disagree about emptiness. Two things empty the group: the registry
 * homing only unlabelled items into it (an unlabelled control renders as a
 * blank row — MOVE_RESET_PTZ is unlabelled on 311 models after run 5) and
 * runtime conditions hiding every item. `overrides` are the sheet's unsaved
 * edits, so the conditions match what the panel would show.
 * See knowledge: advanced-settings-tab-has-five-gates (ai.knowledge/studio.client)
 * — the tab and its strip must be judged empty by one predicate.
 *
 * @param deviceId - The device ID
 * @param overrides - Unsaved device-setting edits from the settings sheet
 * @returns True when the Controls group would render at least one item
 */
export const useHasVisibleControls = (
  deviceId: Device['id'],
  overrides?: Record<string, DeepPartial<PBDeviceSetting>>
): boolean => {
  const { conditionsMap, interfaceSections } = useWiring(deviceId, overrides);
  const sortedControls = useSortedSectionsForSectionGroupKey(deviceId, CONTROLS_REGISTRY_GROUP);

  return useMemo(
    () => selectVisibleSectionLabels(sortedControls, interfaceSections, conditionsMap).length > 0,
    [conditionsMap, interfaceSections, sortedControls]
  );
};

/**
 * Custom hook to get the registry-derived section groups for a device.
 * Now derives section groups from the new flat ADV_SETTINGS structure.
 *
 * The result is only half of the tab strip: `orderSettingsTabs` merges it with
 * the predefined tabs and applies the embedded rule. Ordering here is retained
 * so the two halves arrive already canonical.
 *
 * @param deviceId - The device ID
 * @returns The registry-derived section groups for the device in canonical order
 */
export const useSectionGroups = (deviceId: Device['id']): Array<SettingsTab> => {
  const advancedSettings = useAdvancedSettings(deviceId);
  const deviceCapabilities = useDeviceCapabilities(deviceId);

  // Get all available section groups from advanced settings
  const availableSectionGroups = new Set(
    advancedSettings.map((section) => getSectionGroupFromSectionType(section.type)).filter(Boolean)
  );

  // Apply canonical ordering: filter the canonical order to only include available sections.
  // 'Control' is skipped because its tab comes from PREDEFINED_SECTIONS: deriving it here
  // would give it the panel value 'c' instead of Panel.Control, and a tab whose value is
  // outside the Panel enum renders but is inert on click.
  const orderedSectionGroups = CANONICAL_TAB_ORDER.filter(
    (sectionGroup) =>
      !PREDEFINED_SECTION_GROUPS.has(sectionGroup) && availableSectionGroups.has(sectionGroup)
  );

  // TODO: ***fixme -- for now, leaving it like this because the `deviceCapabilities` is not working correctly
  if (!deviceCapabilities || true) {
    return orderedSectionGroups.map((sectionGroup) => ({
      sectionGroup,
      panelEnumValue: sectionGroup.charAt(0).toLowerCase()
    }));
  }

  // Filter section groups based on device capabilities while maintaining canonical order
  // NOTE: This code path is currently unreachable due to the TODO above, but preserved for future use
  return orderedSectionGroups
    .filter((sectionGroup) => {
      // Skip filtering if section group is not a string
      if (typeof sectionGroup !== 'string') return false;

      // Convert to camelCase format that likely matches capabilities object properties
      // e.g., "Audio" -> "hasAudio", "Video" -> "hasVideo"
      let capabilityKey = `has${sectionGroup}`;
      if (sectionGroup === 'Control') {
        capabilityKey = 'hasMovement';
      }

      // Type-safe check: only filter out section groups where the capability explicitly exists and is false
      // Use optional chaining and type assertion to safely access the property
      return !!(deviceCapabilities as Record<string, unknown>)?.[capabilityKey];
    })
    .map((sectionGroup) => ({
      sectionGroup,
      panelEnumValue: sectionGroup.charAt(0).toLowerCase()
    }));
};

// REMOVED: useSections - no longer used after migration to GenericSettingsSection

/**
 * Custom hook to get the parent state values for a device
 *
 * @param deviceId - The device ID
 * @returns The parent state values for the device
 */
export const useDeviceCapabilities = (deviceId: Device['id']) => {
  const agent = locateProxy(deviceId);
  const capabilities = agent?.state?.status?.device[deviceId]?.capabilities?.overview;

  // TODO: TAM implement fallback logic that uses are current flow for detect capabilities with urX

  return capabilities;
};

/**
 * Helper function to check if a section has meaningful content
 * @param section - The interface section to check
 *
 * currently not being used
 *
 * @returns True if the section has items, false otherwise
 */
export const sectionHasContent = (section: any): boolean => {
  if (!section || !section.items) return false;

  const items = section.items || {};
  const itemCount = Object.keys(items).length;

  // Additional validation: check if items have meaningful data
  if (itemCount === 0) return false;

  // Check if any item has a valid key or command
  const hasValidItems = Object.values(items).some(
    (item: any) => item && (item.key || item.command || item.label)
  );

  return hasValidItems;
};

/**
 * Custom hook that maps section groups to their corresponding sections.
 * Updated for the new flat Device Registry structure.
 *
 * This hook takes interface sections and returns a structured object where:
 * - Each unique section group (derived from section type) becomes a top-level key in the result
 * - Under each top-level key, all corresponding section labels are grouped as keys with a value of true
 *
 * @param deviceId - The device ID
 * @returns An object mapping section group names to their associated section labels
 */
export const useSectionGroupToSectionsMapping = (deviceId: Device['id']) => {
  const agent = locateProxy(deviceId);
  const { interfaceSections } = agent?.state.status?.device[deviceId]?.deviceSettings ?? {};

  return useMemo(() => {
    // Initialize the result object
    const result: Record<string, Record<string, boolean>> = {};

    // Process each interface section
    Object.values(interfaceSections ?? {}).forEach((section) => {
      // Skip sections that are not advanced settings
      if (!isAdvancedSettingsCategory(section.type) || !section.label) return;

      // Get the section group from the section type
      const sectionGroup = getSectionGroupFromSectionType(section.type);
      const sectionLabel = section.label;

      // Initialize the section group object if it doesn't exist yet
      if (!result[sectionGroup]) {
        result[sectionGroup] = {};
      }

      // Add the section label to the section group object
      result[sectionGroup]![sectionLabel] = true;
    });

    return result;
  }, [interfaceSections]); // Only recompute when interfaceSections change
};

export const useSortedSectionsForSectionGroupKey = (
  deviceId: Device['id'],
  sectionGroupPanel: string
) => {
  const agent = locateProxy(deviceId);
  const { interfaceSections } = agent?.state.status?.device[deviceId]?.deviceSettings ?? {};
  const sectionGroupsMappedToSections = useSectionGroupToSectionsMapping(deviceId);

  return useMemo(() => {
    const sectionEntries = sectionGroupsMappedToSections[sectionGroupPanel];

    // Create a lookup map for O(1) access to interface sections by label
    const sectionLookupMap = new Map<string, any>();
    Object.values(interfaceSections ?? {}).forEach((section) => {
      if (section.label) {
        sectionLookupMap.set(section.label, section);
      }
    });

    return Object.keys(sectionEntries ?? {}).sort((sectionA, sectionB) => {
      // Always put General first
      if (sectionA === 'General') return -1;
      if (sectionB === 'General') return 1;

      // O(1) lookup instead of O(n) find operations
      const interfaceSectionA = sectionLookupMap.get(sectionA);
      const interfaceSectionB = sectionLookupMap.get(sectionB);

      // Get sort weights (default to 0 if not found)
      const weightA = interfaceSectionA?.sortWeight ?? 0;
      const weightB = interfaceSectionB?.sortWeight ?? 0;

      // Sort by weight in ascending order (consistent with sortWeightedItems)
      if (weightA !== weightB) {
        return weightA - weightB;
      }

      // Fallback to alphabetical order when weights are equal
      return sectionA.localeCompare(sectionB);
    });
  }, [sectionGroupsMappedToSections, sectionGroupPanel, interfaceSections]);
};
